import * as ss from 'simple-statistics';

export const mean = (arr) => arr.length ? ss.mean(arr) : 0;
export const median = (arr) => arr.length ? ss.median(arr) : 0;
export const mode = (arr) => arr.length ? ss.mode(arr) : 0;
export const variance = (arr) => arr.length > 1 ? ss.variance(arr) : 0;
export const standardDeviation = (arr) => arr.length > 1 ? ss.standardDeviation(arr) : 0;
export const sum = (arr) => arr.reduce((a, b) => a + b, 0);

export const zScore = (value, mean, sd) => sd !== 0 ? (value - mean) / sd : 0;

export const normalCDF = (z) => {
  const a1 = 0.254829592, a2 = -0.284496736, a3 = 1.421413741;
  const a4 = -1.453152027, a5 = 1.061405429, p = 0.3275911;
  const sign = z < 0 ? -1 : 1;
  z = Math.abs(z) / Math.sqrt(2);
  const t = 1.0 / (1.0 + p * z);
  const y = 1.0 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-z * z);
  return 0.5 * (1.0 + sign * y);
};

export const tDistCDF = (t, df) => {
  const x = df / (df + t * t);
  let p = 0.5 * incompleteBeta(x, df / 2, 0.5);
  return t > 0 ? 1 - p : p;
};

const incompleteBeta = (x, a, b) => {
  if (x === 0) return 0;
  if (x === 1) return 1;
  const bt = Math.exp(lgamma(a + b) - lgamma(a) - lgamma(b) + a * Math.log(x) + b * Math.log(1 - x));
  if (x < (a + 1) / (a + b + 2)) {
    return bt * betaCF(x, a, b) / a;
  }
  return 1 - bt * betaCF(1 - x, b, a) / b;
};

const betaCF = (x, a, b) => {
  const maxIter = 100, eps = 1e-14;
  let qab = a + b, qap = a + 1, qam = a - 1;
  let c = 1, d = 1 - qab * x / qap;
  if (Math.abs(d) < 1e-30) d = 1e-30;
  d = 1 / d;
  let h = d;
  for (let m = 1; m <= maxIter; m++) {
    const m2 = 2 * m;
    let aa = m * (b - m) * x / ((qam + m2) * (a + m2));
    d = 1 + aa * d;
    if (Math.abs(d) < 1e-30) d = 1e-30;
    c = 1 + aa / c;
    if (Math.abs(c) < 1e-30) c = 1e-30;
    d = 1 / d;
    h *= d * c;
    aa = -(a + m) * (qab + m) * x / ((a + m2) * (qap + m2));
    d = 1 + aa * d;
    if (Math.abs(d) < 1e-30) d = 1e-30;
    c = 1 + aa / c;
    if (Math.abs(c) < 1e-30) c = 1e-30;
    d = 1 / d;
    const del = d * c;
    h *= del;
    if (Math.abs(del - 1) < eps) break;
  }
  return h;
};

const lgamma = (x) => {
  const c = [76.18009172947146, -86.50532032941677, 24.01409824083091, -1.231739572450155, 0.001208650973866179, -0.000005395239384953];
  let y = x, tmp = x + 5.5;
  tmp -= (x + 0.5) * Math.log(tmp);
  let ser = 1.000000000190015;
  for (let j = 0; j < 6; j++) ser += c[j] / ++y;
  return -tmp + Math.log(2.5066282746310005 * ser / x);
};

export const chiSquaredCDF = (x, df) => {
  if (x <= 0) return 0;
  return lowerIncompleteGamma(df / 2, x / 2) / gamma(df / 2);
};

const lowerIncompleteGamma = (a, x) => {
  if (x < 0) return 0;
  if (x === 0) return 0;
  let sum = 0, term = 1 / a;
  for (let n = 1; n < 100; n++) {
    sum += term;
    term *= x / (a + n);
    if (Math.abs(term) < 1e-10) break;
  }
  return Math.pow(x, a) * Math.exp(-x) * sum;
};

const gamma = (z) => Math.exp(lgamma(z));

export const fDistCDF = (f, df1, df2) => {
  if (f <= 0) return 0;
  const x = (df1 * f) / (df1 * f + df2);
  return incompleteBeta(x, df1 / 2, df2 / 2);
};

export const independentTTest = (group1, group2) => {
  if (group1.length < 2 || group2.length < 2) return null;
  
  const n1 = group1.length, n2 = group2.length;
  const mean1 = mean(group1), mean2 = mean(group2);
  const var1 = variance(group1), var2 = variance(group2);
  const pooledSE = Math.sqrt(var1 / n1 + var2 / n2);
  
  if (pooledSE === 0) return null;
  
  const t = (mean1 - mean2) / pooledSE;
  const df = Math.pow(var1 / n1 + var2 / n2, 2) / 
    (Math.pow(var1 / n1, 2) / (n1 - 1) + Math.pow(var2 / n2, 2) / (n2 - 1));
  const p = 2 * (1 - tDistCDF(Math.abs(t), df));
  const cohenD = (mean1 - mean2) / Math.sqrt((var1 + var2) / 2);
  
  return { t, df, p, mean1, mean2, sd1: Math.sqrt(var1), sd2: Math.sqrt(var2), n1, n2, cohenD };
};

export const pairedTTest = (before, after) => {
  if (before.length !== after.length || before.length < 2) return null;
  
  const differences = before.map((b, i) => after[i] - b);
  const n = differences.length;
  const meanDiff = mean(differences);
  const sdDiff = standardDeviation(differences);
  const se = sdDiff / Math.sqrt(n);
  
  if (se === 0) return null;
  
  const t = meanDiff / se;
  const df = n - 1;
  const p = 2 * (1 - tDistCDF(Math.abs(t), df));
  const cohenD = meanDiff / sdDiff;
  
  return { t, df, p, meanDiff, sdDiff, n, cohenD };
};

export const oneWayAnova = (groups) => {
  if (groups.length < 2) return null;
  const allData = groups.flat();
  const grandMean = mean(allData);
  const N = allData.length;
  const k = groups.length;
  
  let ssBetween = 0, ssWithin = 0;
  groups.forEach(group => {
    const groupMean = mean(group);
    ssBetween += group.length * Math.pow(groupMean - grandMean, 2);
    group.forEach(val => {
      ssWithin += Math.pow(val - groupMean, 2);
    });
  });
  
  const dfBetween = k - 1;
  const dfWithin = N - k;
  const msBetween = ssBetween / dfBetween;
  const msWithin = ssWithin / dfWithin;
  const F = msBetween / msWithin;
  const p = 1 - fDistCDF(F, dfBetween, dfWithin);
  const etaSquared = ssBetween / (ssBetween + ssWithin);
  
  return { F, dfBetween, dfWithin, p, ssBetween, ssWithin, msBetween, msWithin, etaSquared };
};

export const chiSquareTest = (observed, expected = null) => {
  if (!expected) {
    const total = sum(observed);
    expected = observed.map(() => total / observed.length);
  }
  
  let chiSq = 0;
  for (let i = 0; i < observed.length; i++) {
    if (expected[i] !== 0) {
      chiSq += Math.pow(observed[i] - expected[i], 2) / expected[i];
    }
  }
  
  const df = observed.length - 1;
  const p = 1 - chiSquaredCDF(chiSq, df);
  const cramersV = Math.sqrt(chiSq / (sum(observed) * (Math.min(observed.length, 1) - 1)));
  
  return { chiSquare: chiSq, df, p, cramersV };
};

export const chiSquareIndependence = (contingencyTable) => {
  const rows = contingencyTable.length;
  const cols = contingencyTable[0].length;
  const rowTotals = contingencyTable.map(row => sum(row));
  const colTotals = contingencyTable[0].map((_, j) => sum(contingencyTable.map(row => row[j])));
  const total = sum(rowTotals);
  
  let chiSq = 0;
  for (let i = 0; i < rows; i++) {
    for (let j = 0; j < cols; j++) {
      const expected = (rowTotals[i] * colTotals[j]) / total;
      if (expected > 0) {
        chiSq += Math.pow(contingencyTable[i][j] - expected, 2) / expected;
      }
    }
  }
  
  const df = (rows - 1) * (cols - 1);
  const p = 1 - chiSquaredCDF(chiSq, df);
  const cramersV = Math.sqrt(chiSq / (total * (Math.min(rows, cols) - 1)));
  
  return { chiSquare: chiSq, df, p, cramersV };
};

export const pearsonCorrelation = (x, y) => {
  if (x.length !== y.length || x.length < 3) return null;
  const r = ss.sampleCorrelation(x, y);
  const n = x.length;
  const t = r * Math.sqrt((n - 2) / (1 - r * r));
  const df = n - 2;
  const p = 2 * (1 - tDistCDF(Math.abs(t), df));
  return { r, t, df, p, n };
};

export const spearmanCorrelation = (x, y) => {
  if (x.length !== y.length || x.length < 3) return null;
  const rankX = getRanks(x);
  const rankY = getRanks(y);
  return pearsonCorrelation(rankX, rankY);
};

const getRanks = (arr) => {
  const sorted = arr.map((v, i) => ({ v, i })).sort((a, b) => a.v - b.v);
  const ranks = new Array(arr.length);
  let i = 0;
  while (i < sorted.length) {
    let j = i;
    while (j < sorted.length && sorted[j].v === sorted[i].v) j++;
    const avgRank = (i + j + 1) / 2;
    for (let k = i; k < j; k++) ranks[sorted[k].i] = avgRank;
    i = j;
  }
  return ranks;
};

export const simpleLinearRegression = (x, y) => {
  if (x.length !== y.length || x.length < 3) return null;
  const result = ss.linearRegression(x.map((xi, i) => [xi, y[i]]));
  const predictions = x.map(xi => result.m * xi + result.b);
  const ssRes = sum(y.map((yi, i) => Math.pow(yi - predictions[i], 2)));
  const ssTot = sum(y.map(yi => Math.pow(yi - mean(y), 2)));
  const rSquared = 1 - ssRes / ssTot;
  const n = x.length;
  const se = Math.sqrt(ssRes / (n - 2));
  
  return {
    slope: result.m,
    intercept: result.b,
    rSquared,
    standardError: se,
    n
  };
};

export const multipleLinearRegression = (X, y) => {
  const n = y.length;
  const p = X[0].length;
  
  const XWithIntercept = X.map(row => [1, ...row]);
  const XT = transpose(XWithIntercept);
  const XTX = matMul(XT, XWithIntercept);
  const XTXInv = invertMatrix(XTX);
  if (!XTXInv) return null;
  
  const XTy = matVec(XT, y);
  const beta = matVec(XTXInv, XTy);
  
  const predictions = XWithIntercept.map(row => sum(row.map((x, j) => x * beta[j])));
  const residuals = y.map((yi, i) => yi - predictions[i]);
  const ssRes = sum(residuals.map(r => r * r));
  const ssTot = sum(y.map(yi => Math.pow(yi - mean(y), 2)));
  const rSquared = 1 - ssRes / ssTot;
  const adjustedRSquared = 1 - (1 - rSquared) * (n - 1) / (n - p - 1);
  
  return {
    coefficients: beta,
    rSquared,
    adjustedRSquared,
    n,
    predictors: p
  };
};

const transpose = (m) => m[0].map((_, i) => m.map(row => row[i]));
const matMul = (a, b) => a.map(row => b[0].map((_, j) => sum(row.map((v, k) => v * b[k][j]))));
const matVec = (m, v) => m.map(row => sum(row.map((x, i) => x * v[i])));

const invertMatrix = (m) => {
  const n = m.length;
  const aug = m.map((row, i) => [...row, ...Array(n).fill(0).map((_, j) => i === j ? 1 : 0)]);
  
  for (let i = 0; i < n; i++) {
    let maxRow = i;
    for (let k = i + 1; k < n; k++) {
      if (Math.abs(aug[k][i]) > Math.abs(aug[maxRow][i])) maxRow = k;
    }
    [aug[i], aug[maxRow]] = [aug[maxRow], aug[i]];
    
    if (Math.abs(aug[i][i]) < 1e-10) return null;
    
    const pivot = aug[i][i];
    for (let j = 0; j < 2 * n; j++) aug[i][j] /= pivot;
    
    for (let k = 0; k < n; k++) {
      if (k !== i) {
        const factor = aug[k][i];
        for (let j = 0; j < 2 * n; j++) aug[k][j] -= factor * aug[i][j];
      }
    }
  }
  
  return aug.map(row => row.slice(n));
};

export const mannWhitneyU = (group1, group2) => {
  const n1 = group1.length, n2 = group2.length;
  const combined = [...group1.map(v => ({ v, g: 1 })), ...group2.map(v => ({ v, g: 2 }))];
  combined.sort((a, b) => a.v - b.v);
  
  let rank = 1;
  for (let i = 0; i < combined.length; i++) {
    let j = i;
    while (j < combined.length && combined[j].v === combined[i].v) j++;
    const avgRank = (rank + rank + j - i - 1) / 2;
    for (let k = i; k < j; k++) combined[k].rank = avgRank;
    rank += j - i;
    i = j - 1;
  }
  
  const R1 = sum(combined.filter(c => c.g === 1).map(c => c.rank));
  const U1 = n1 * n2 + (n1 * (n1 + 1)) / 2 - R1;
  const U2 = n1 * n2 - U1;
  const U = Math.min(U1, U2);
  
  const muU = (n1 * n2) / 2;
  const sigmaU = Math.sqrt((n1 * n2 * (n1 + n2 + 1)) / 12);
  const z = (U - muU) / sigmaU;
  const p = 2 * (1 - normalCDF(Math.abs(z)));
  
  return { U, z, p, n1, n2 };
};

export const wilcoxonSignedRank = (before, after) => {
  if (before.length !== after.length) return null;
  
  const differences = before.map((b, i) => ({ diff: after[i] - b, abs: Math.abs(after[i] - b) }))
    .filter(d => d.diff !== 0);
  
  if (differences.length === 0) return null;
  
  differences.sort((a, b) => a.abs - b.abs);
  let rank = 1;
  for (let i = 0; i < differences.length; i++) {
    let j = i;
    while (j < differences.length && differences[j].abs === differences[i].abs) j++;
    const avgRank = (rank + rank + j - i - 1) / 2;
    for (let k = i; k < j; k++) differences[k].rank = avgRank;
    rank += j - i;
    i = j - 1;
  }
  
  const Wplus = sum(differences.filter(d => d.diff > 0).map(d => d.rank));
  const Wminus = sum(differences.filter(d => d.diff < 0).map(d => d.rank));
  const W = Math.min(Wplus, Wminus);
  
  const n = differences.length;
  const muW = n * (n + 1) / 4;
  const sigmaW = Math.sqrt(n * (n + 1) * (2 * n + 1) / 24);
  const z = (W - muW) / sigmaW;
  const p = 2 * (1 - normalCDF(Math.abs(z)));
  
  return { W, z, p, n };
};

export const kruskalWallis = (groups) => {
  const combined = [];
  groups.forEach((group, gi) => {
    group.forEach(v => combined.push({ v, g: gi }));
  });
  combined.sort((a, b) => a.v - b.v);
  
  let rank = 1;
  for (let i = 0; i < combined.length; i++) {
    let j = i;
    while (j < combined.length && combined[j].v === combined[i].v) j++;
    const avgRank = (rank + rank + j - i - 1) / 2;
    for (let k = i; k < j; k++) combined[k].rank = avgRank;
    rank += j - i;
    i = j - 1;
  }
  
  const N = combined.length;
  const rankSums = groups.map((_, gi) => sum(combined.filter(c => c.g === gi).map(c => c.rank)));
  
  let H = 0;
  groups.forEach((group, gi) => {
    H += Math.pow(rankSums[gi], 2) / group.length;
  });
  H = (12 / (N * (N + 1))) * H - 3 * (N + 1);
  
  const df = groups.length - 1;
  const p = 1 - chiSquaredCDF(H, df);
  
  return { H, df, p };
};

export const cronbachAlpha = (items) => {
  const k = items.length;
  if (k < 2) return null;
  
  const itemVars = items.map(item => variance(item));
  const sumItemVars = sum(itemVars);
  
  const totals = items[0].map((_, i) => sum(items.map(item => item[i])));
  const totalVar = variance(totals);
  
  if (totalVar === 0) return null;
  
  const alpha = (k / (k - 1)) * (1 - sumItemVars / totalVar);
  
  return { alpha, k, sumItemVars, totalVar };
};

export const proportionTest = (successes, n, p0 = 0.5) => {
  const pHat = successes / n;
  const se = Math.sqrt((p0 * (1 - p0)) / n);
  const z = (pHat - p0) / se;
  const p = 2 * (1 - normalCDF(Math.abs(z)));
  
  return { pHat, z, p, n };
};

export const twoProportionZTest = (x1, n1, x2, n2) => {
  const p1 = x1 / n1;
  const p2 = x2 / n2;
  const pPooled = (x1 + x2) / (n1 + n2);
  const se = Math.sqrt(pPooled * (1 - pPooled) * (1/n1 + 1/n2));
  const z = (p1 - p2) / se;
  const p = 2 * (1 - normalCDF(Math.abs(z)));
  
  return { p1, p2, z, p, diff: p1 - p2 };
};

export const fisherExactTest = (a, b, c, d) => {
  const factorial = (n) => n <= 1 ? 1 : n * factorial(n - 1);
  const n = a + b + c + d;
  const p = (factorial(a + b) * factorial(c + d) * factorial(a + c) * factorial(b + d)) /
            (factorial(n) * factorial(a) * factorial(b) * factorial(c) * factorial(d));
  return { p: Math.min(p * 2, 1), a, b, c, d };
};

export const mcnemarTest = (b, c) => {
  if (b + c === 0) return null;
  const chiSq = Math.pow(Math.abs(b - c) - 1, 2) / (b + c);
  const p = 1 - chiSquaredCDF(chiSq, 1);
  return { chiSquare: chiSq, p, b, c };
};
